import { ChangeDetectionStrategy, Component, computed, input, inject, InjectionToken, TemplateRef } from '@angular/core';
import { fadeInOnEnterAnimation, fadeOutOnLeaveAnimation, slideInUpOnEnterAnimation, slideOutDownOnLeaveAnimation, slideInDownOnEnterAnimation, slideOutUpOnLeaveAnimation, slideInLeftOnEnterAnimation, slideOutRightOnLeaveAnimation, slideInRightOnEnterAnimation, slideOutLeftOnLeaveAnimation } from '../animations/index';
import { NgTemplateOutlet } from '@angular/common';

export const TOOLTIP_CONTENT = new InjectionToken<string | null>('TOOLTIP_CONTENT');
export const TOOLTIP_TEMPLATE = new InjectionToken<TemplateRef<unknown>>('TOOLTIP_TEMPLATE');
export const TOOLTIP_DATA = new InjectionToken<unknown>('TOOLTIP_DATA');

@Component({
    selector: 'app-tooltip',
    templateUrl: './tooltip.component.html',
    styleUrls: ['./tooltip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet],
    animations: [
        fadeInOnEnterAnimation({ duration: 200 }),
        fadeOutOnLeaveAnimation({ duration: 150 }),
        slideInUpOnEnterAnimation({ duration: 200, translate: '8px' }),
        slideOutDownOnLeaveAnimation({ duration: 150, translate: '8px' }),
        slideInDownOnEnterAnimation({ duration: 200, translate: '8px' }),
        slideOutUpOnLeaveAnimation({ duration: 150, translate: '8px' }),
        slideInLeftOnEnterAnimation({ duration: 200, translate: '8px' }),
        slideOutRightOnLeaveAnimation({ duration: 150, translate: '8px' }),
        slideInRightOnEnterAnimation({ duration: 200, translate: '8px' }),
        slideOutLeftOnLeaveAnimation({ duration: 150, translate: '8px' })
    ]
})
export class TooltipComponent {
  content = input<string | null>(null);
  contentTemplate = input<TemplateRef<unknown> | null>(null);
  maxWidth = input('400px');
  position = input('top');

  private injectedContent = inject(TOOLTIP_CONTENT, { optional: true });
  injectedTemplate = inject(TOOLTIP_TEMPLATE, { optional: true });
  private injectedData = inject(TOOLTIP_DATA, { optional: true });

  displayContent = computed(() => this.injectedContent ?? this.content());

  displayTemplate = computed(() => this.injectedTemplate || this.contentTemplate());

  isTemplate = computed(() => !!this.displayTemplate());

  animationName = computed(() => {
    switch (this.position()) {
      case 'top':
      case 'top-start':
      case 'top-end':
        return 'slideInUpOnEnter';
      case 'bottom':
      case 'bottom-start':
      case 'bottom-end':
        return 'slideInDownOnEnter';
      case 'left':
      case 'left-start':
      case 'left-end':
        return 'slideInLeftOnEnter';
      case 'right':
      case 'right-start':
      case 'right-end':
        return 'slideInRightOnEnter';
      default:
        return 'fadeInOnEnter';
    }
  });

  leaveAnimationName = computed(() => {
    switch (this.position()) {
      case 'top':
      case 'top-start':
      case 'top-end':
        return 'slideOutDownOnLeave';
      case 'bottom':
      case 'bottom-start':
      case 'bottom-end':
        return 'slideOutUpOnLeave';
      case 'left':
      case 'left-start':
      case 'left-end':
        return 'slideOutRightOnLeave';
      case 'right':
      case 'right-start':
      case 'right-end':
        return 'slideOutLeftOnLeave';
      default:
        return 'fadeOutOnLeave';
    }
  });
}
